import { parseDate } from '../helpers/parse-date';
import isFalsy from '../helpers/is-falsy';
import isTruthy from '../helpers/is-truthy';

// Raw enum ids accepted by the two revenue columns. These mirror the ids in
// api/lookups/revenue-recognition-methods.js and api/lookups/revenue-attribution-methods.js;
// rules files are front-end and cannot import from api/lookups, so the ids are duplicated here.
const REVENUE_RECOGNITION_METHOD_IDS = [
  'invoiced',
  'manual',
  'automated_hours',
  'automated_services_cost',
  'automated_tm_basis',
];
const REVENUE_ATTRIBUTION_METHOD_IDS = ['hours', 'services_cost', 'tm_basis'];
const BILLING_TYPE_IDS = ['tm', 'fixed', 'fixed_recurring', 'non_billable'];
const STATUS_IDS = ['tentative', 'not_started', 'in_progress', 'paused', 'completed', 'cancelled'];

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';
const isBoolean = (value) => isBlank(value) || isTruthy(value) || isFalsy(value);

const projects = {
  'Import ID': {
    requirementType: 'required',
    description:
      'Unique identifier for the imported project. Imported row will be checked for a duplicate; if the same Import ID was previously imported, the row is skipped. Stored as the project legacy id.',
    dataType: 'string',
    validator: (value) => !isBlank(value),
  },
  'Project Code': {
    requirementType: 'optional',
    renamedFrom: 'ID',
    description:
      'Project code shown in the app. Leave blank to auto-generate. Previously named "ID"; the "ID" column no longer sets the legacy id — use "Import ID" for that.',
    dataType: 'string',
    validator: (value) => isBlank(value) || String(value).trim().length <= 255,
  },
  'Client Name': {
    requirementType: 'required',
    description:
      'Name of the client that owns this project. Must match an existing client name exactly, including spaces, punctuation and capitalization, otherwise the row is skipped during import.',
    dataType: 'string',
    validator: (value) => !isBlank(value),
  },
  'Project Name': {
    requirementType: 'required',
    description: 'Name of the project.',
    dataType: 'string',
    validator: (value) => !isBlank(value),
  },
  'Billing Type': {
    requirementType: 'optional',
    description:
      'One of: tm, fixed, fixed_recurring, non_billable (Time & Materials, Fixed Fee, Fixed Monthly, Non-Billable). Defaults to tm when blank.',
    dataType: 'string',
    validator: (value) => isBlank(value) || BILLING_TYPE_IDS.includes(String(value).trim()),
  },
  'Use Roles': {
    requirementType: 'optional',
    description: 'Set to "Yes" or "No". Leave blank to use the workspace default.',
    dataType: 'boolean',
    validator: isBoolean,
  },
  'Use Overall Budget': {
    requirementType: 'optional',
    renamedFrom: 'Use Budget',
    description:
      'Set to "Yes" or "No" to track an overall project budget. Leave blank to use the workspace default. Previously named "Use Budget".',
    dataType: 'boolean',
    validator: isBoolean,
  },
  'Use Monthly Budget': {
    requirementType: 'optional',
    description:
      'Set to "Yes" or "No" to track a monthly budget. A fixed-fee project (Billing Type "fixed") can only set this to "Yes" when "Use Overall Budget" is also "Yes".',
    dataType: 'boolean',
    validator: (value, row) => {
      if (isBlank(value)) return true;
      if (!isTruthy(value) && !isFalsy(value)) return false;
      // Cross-field rule mirroring Project.GraphSchema (api/models/Project.js): a fixed-fee
      // project must track an overall budget in order to track a monthly budget.
      if (isTruthy(value) && String(row['Billing Type'] ?? '').trim() === 'fixed') {
        return isTruthy(row['Use Overall Budget']);
      }
      return true;
    },
  },
  Notes: {
    requirementType: 'optional',
    renamedFrom: 'Description',
    description: 'Free-text notes for the project (max 5000 characters). Previously named "Description".',
    dataType: 'string',
    validator: (value) => isBlank(value) || String(value).length <= 5000,
  },
  Practice: {
    requirementType: 'optional',
    description:
      'Name of an existing Practice to assign. Must match exactly on capitalization, spacing and punctuation, otherwise it is left unset.',
    dataType: 'string',
    validator: () => true,
  },
  'Business Unit': {
    requirementType: 'optional',
    description:
      'Name of an existing Business Unit to assign. Must match exactly on capitalization, spacing and punctuation, otherwise it is left unset.',
    dataType: 'string',
    validator: () => true,
  },
  'Project Type': {
    requirementType: 'optional',
    description:
      'Name of an existing Project Type. Must match exactly on capitalization, spacing and punctuation, otherwise it is left unset.',
    dataType: 'string',
    validator: () => true,
  },
  'Project Group': {
    requirementType: 'optional',
    description:
      'Name of a project group for the client. Requires the Project Groups workspace feature; the group is created if it does not already exist.',
    dataType: 'string',
    validator: () => true,
  },
  'Sales Representative (email)': {
    requirementType: 'optional',
    description:
      'Email address of the workspace member to set as the sales representative. Must match an existing member email exactly; if provided but unmatched, the row is skipped.',
    dataType: 'email',
    validator: (value) => isBlank(value) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim()),
  },
  Start: {
    requirementType: 'optional',
    description: 'Project start date in "YYYY-MM-DD" or "MM/DD/YYYY" format.',
    dataType: 'date',
    validator: (value) => isBlank(value) || !!parseDate(String(value).trim()),
  },
  End: {
    requirementType: 'optional',
    description: 'Project end date in "YYYY-MM-DD" or "MM/DD/YYYY" format.',
    dataType: 'date',
    validator: (value) => isBlank(value) || !!parseDate(String(value).trim()),
  },
  'PO Number': {
    requirementType: 'optional',
    description: 'Purchase order number associated with the project.',
    dataType: 'string',
    validator: (value) => isBlank(value) || String(value).trim().length <= 255,
  },
  'Admin Email': {
    requirementType: 'optional',
    description:
      'Semicolon-separated list of member email addresses to assign as project administrators. Must match existing member emails. If none match, the importing member is assigned.',
    dataType: 'string',
    validator: (value) =>
      isBlank(value) ||
      String(value)
        .split(';')
        .every((email) => email.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())),
  },
  'Summary Budget': {
    requirementType: 'optional',
    description: 'Estimated services revenue for the whole project (numeric).',
    dataType: 'number',
    validator: (value) => {
      if (isBlank(value)) return true;
      const num = Number(String(value).replace(/[^\d.-]/g, ''));
      return Number.isFinite(num) && num >= 0;
    },
  },
  'Summary Budget Hours': {
    requirementType: 'optional',
    description: 'Estimated billable hours for the whole project (numeric).',
    dataType: 'number',
    validator: (value) => {
      if (isBlank(value)) return true;
      const num = Number(String(value).replace(/[^\d.-]/g, ''));
      return Number.isFinite(num) && num >= 0;
    },
  },
  'Strict Roles': {
    requirementType: 'optional',
    description: 'Set to "Yes" or "No" to require time entries to be tracked against assigned roles only.',
    dataType: 'boolean',
    validator: isBoolean,
  },
  'Track Project Health': {
    requirementType: 'optional',
    description: 'Set to "Yes" or "No" to enable project health reports. Leave blank to use the workspace default.',
    dataType: 'boolean',
    validator: isBoolean,
  },
  'Require Notes': {
    requirementType: 'optional',
    description: 'Set to "Yes" or "No" to require notes on every time entry. Leave blank to use the workspace default.',
    dataType: 'boolean',
    validator: isBoolean,
  },
  'Require Task': {
    requirementType: 'optional',
    description:
      'Set to "Yes" or "No" to require a task on every time entry. Leave blank to use the workspace default.',
    dataType: 'boolean',
    validator: isBoolean,
  },
  'Revenue Recognition Method': {
    requirementType: 'optional',
    description:
      'One of: invoiced (As Invoiced), manual (Manual), automated_hours (Automated - Percentage of Billable Hours), automated_services_cost (Automated - Percentage of Services Cost), automated_tm_basis (Automated - Percentage of Time & Materials Revenue). Leave blank to use the workspace default.',
    dataType: 'string',
    validator: (value) => isBlank(value) || REVENUE_RECOGNITION_METHOD_IDS.includes(String(value).trim()),
  },
  'Revenue Attribution Method': {
    requirementType: 'optional',
    description:
      'One of: hours (Percentage of Billable Hours), services_cost (Percentage of Services Cost), tm_basis (Percentage of Time & Materials Revenue). Leave blank to use the workspace default.',
    dataType: 'string',
    validator: (value) => isBlank(value) || REVENUE_ATTRIBUTION_METHOD_IDS.includes(String(value).trim()),
  },
  'Accounting Notes': {
    requirementType: 'optional',
    description: 'Free-text accounting notes for the project (max 5000 characters).',
    dataType: 'string',
    validator: (value) => isBlank(value) || String(value).length <= 5000,
  },
  Tags: {
    requirementType: 'optional',
    description: 'Semicolon-separated list of tags to associate with the project (e.g. "North America;Tier 1").',
    dataType: 'string',
    validator: () => true,
  },
  Status: {
    requirementType: 'optional',
    description:
      'One of: tentative, not_started, in_progress, paused, completed, cancelled. Defaults to not_started when blank.',
    dataType: 'string',
    validator: (value) => isBlank(value) || STATUS_IDS.includes(String(value).trim()),
  },
  Archived: {
    requirementType: 'optional',
    description: 'Set to "Yes" or "No" to archive the project on import.',
    dataType: 'boolean',
    validator: isBoolean,
  },
  Locked: {
    requirementType: 'optional',
    description: 'Set to "Yes" or "No" to lock time and expenses on the project.',
    dataType: 'boolean',
    validator: isBoolean,
  },
  'Approval Method': {
    requirementType: 'optional',
    description:
      'One of: auto, manual (case-insensitive). "auto" auto-approves time and expenses; "manual" or blank keeps manual approval.',
    dataType: 'string',
    validator: (value) => isBlank(value) || ['auto', 'manual'].includes(String(value).trim().toLowerCase()),
  },
  Currency: {
    requirementType: 'optional',
    description: 'Valid 3-letter ISO currency code (e.g. USD, EUR, GBP). Defaults to USD when blank.',
    dataType: 'string',
    validator: (value) => isBlank(value) || /^[A-Z]{3}$/i.test(String(value).trim()),
  },
};

export default projects;
